import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '@/context/AdminContext';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, Users, MessageSquare, AlertTriangle, 
  Activity, LogOut, Sun, Moon, X, Menu, Download, Key
} from 'lucide-react';

export default function AdminSidebar({ currentPath, isOpen, onClose }) {
  const { user, logout } = useAdmin();
  const { isDark, toggleTheme, colors } = useAdminTheme();
  const navigate = useNavigate();

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'Users & CVs', icon: Users },
    { path: '/admin/leads', label: 'Leads & Downloads', icon: Download },
    { path: '/admin/sessions', label: 'Sessions', icon: MessageSquare },
    { path: '/admin/api-keys', label: 'API Keys', icon: Key },
    { path: '/admin/errors', label: 'Error Logs', icon: AlertTriangle },
    { path: '/admin/health', label: 'API Health', icon: Activity },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      onClose?.();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 left-0 h-screen z-50
        w-64 ${colors.bgSidebar} border-r ${colors.borderSidebar} flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo + Close button on mobile */}
        <div className={`p-4 border-b ${colors.borderSidebar} flex items-center justify-between`}>
          <Link to="/admin" className="flex items-center gap-3" onClick={handleNavClick}>
            <img src="/edomatch-icon.png" alt="EdoMatch" className="w-10 h-10" />
            <div>
              <span className={`${colors.text} font-bold text-lg`}>EdoMatch</span>
              <p className={`text-xs ${colors.textMuted}`}>Admin Panel</p>
            </div>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className={`lg:hidden ${colors.textSecondary} hover:${colors.text}`}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Theme Toggle */}
        <div className={`px-4 py-3 border-b ${colors.borderSidebar}`}>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleTheme}
            className={`w-full justify-start gap-2 ${colors.btnOutline}`}
          >
            {isDark ? (
              <>
                <Sun className="w-4 h-4" />
                <span>Light Mode</span>
              </>
            ) : (
              <>
                <Moon className="w-4 h-4" />
                <span>Dark Mode</span>
              </>
            )}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = currentPath === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive 
                  ? 'bg-[#5BA946] text-white' 
                  : `${colors.textSecondary} ${colors.bgHover} hover:${colors.text}`
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* User & Logout */}
      <div className={`p-4 border-t ${colors.borderSidebar}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-[#5BA946] rounded-full flex items-center justify-center text-white font-bold">
            {user?.username?.[0]?.toUpperCase() || 'A'}
          </div>
          <div>
            <p className={`${colors.text} font-medium`}>{user?.username || 'Admin'}</p>
            <p className={`text-xs ${colors.textMuted}`}>{user?.role || 'Administrator'}</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          onClick={handleLogout}
          className={`w-full justify-start ${colors.textSecondary} ${colors.bgHover}`}
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </aside>
    </>
  );
}
